import { ArrowDownLeft, ArrowUpRight, BarChart3, ChevronRight, FileWarning, Info, Plus, Receipt, Scale, Wallet } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { InfoNote } from '@/components/common/Panel'
import { PageHeader } from '@/components/common/PageHeader'
import { ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useOverview } from './api'
import { CategoryDetail } from './components/CategoryDetail'
import { CategoryDonut, IncomeExpenseChart } from './components/Charts'
import { Kpi, Money, PeriodSelect, Section, TrendChip } from './components/FinanceBits'
import { TransactionFormModal } from './components/TransactionFormModal'
import { FINANCE_WRITE_PERM } from './permissions'
import { monthLabel, useFinanceLookups, usePeriod } from './ui'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, Printer } from 'lucide-react'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import { fetchTaxReport, financeKeys } from './api'
import { FINANCE_GL_READ, FINANCE_TAX_READ } from './permissions'
import { printManagementSummary } from './reports'

export function FinanceOverviewPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { month, setMonth } = usePeriod()
  const overview = useOverview(month)
  const { cat } = useFinanceLookups()
  const [txForm, setTxForm] = useState(false)
  const o = overview.data
  const qc = useQueryClient()
  const [summaryBusy, setSummaryBusy] = useState(false)
  // Yönetim özeti vergi ve bordro verisini de ister; yetkisi olmayan bölüm boş geçer.
  const onManagementSummary = async () => {
    if (!o) return
    setSummaryBusy(true)
    try {
      const [tax, mirrors] = await Promise.all([
        can(FINANCE_TAX_READ) ? qc.fetchQuery({ queryKey: financeKeys.tax(month), queryFn: () => fetchTaxReport(month) }).catch(() => undefined) : undefined,
        can(FINANCE_GL_READ) ? api.get<unknown>('/api/v1/finance/payroll-mirrors', { period: month }).catch(() => []) : [],
      ])
      const row = asList(mirrors as Record<string, unknown>[]).find((m) => String(m.Period ?? m.period ?? '') === month)
      const payroll = row ? { gross: String(row.GrossTotal ?? row.gross_total ?? '0'), net: String(row.NetTotal ?? row.net_total ?? '0') } : null
      printManagementSummary(o, tax, payroll, (id) => cat.get(id)?.name ?? '—')
    } finally {
      setSummaryBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.overview.title')}
        description={t('finance.overview.subtitle')}
        actions={
          <>
            <PeriodSelect value={month} onChange={setMonth} />
            <Button variant="outline" disabled={!o || summaryBusy} onClick={() => void onManagementSummary()}>
              {summaryBusy ? <Loader2 className="animate-spin" /> : <Printer />}
              {t('finance.reports.managementButton')}
            </Button>
            {can(FINANCE_WRITE_PERM) ? (
              <Button onClick={() => setTxForm(true)}>
                <Plus />
                {t('finance.tx.add')}
              </Button>
            ) : null}
          </>
        }
      />

      {overview.isError ? (
        <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
      ) : !o ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-28 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-80 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <Kpi icon={ArrowDownLeft} tone="success" label={t('finance.overview.collected')} value={<Money value={o.collected.value} />} footer={<TrendChip trend={o.collected} />} />
            <Kpi icon={ArrowUpRight} tone="danger" label={t('finance.overview.paidOut')} value={<Money value={o.paidOut.value} />} footer={<TrendChip trend={o.paidOut} goodWhenUp={false} />} />
            <Kpi icon={BarChart3} tone="info" label={t('finance.overview.netCash')} value={<Money value={o.netCash.value} signed />} footer={<TrendChip trend={o.netCash} />} />
            <Kpi icon={Receipt} tone="primary" label={t('finance.overview.receivable')} value={<Money value={o.receivable.value} />} footer={t('finance.overview.partiesDocs', { parties: o.receivable.parties, docs: o.receivable.docs, party: t('finance.customers') })} />
            <Kpi icon={Wallet} tone="warning" label={t('finance.overview.payable')} value={<Money value={o.payable.value} />} footer={t('finance.overview.partiesDocs', { parties: o.payable.parties, docs: o.payable.docs, party: t('finance.suppliers') })} />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2 xl:col-span-2">
              <Section title={t('finance.overview.monthly')} icon={BarChart3}>
                <IncomeExpenseChart data={o.monthly} incomeLabel={t('finance.overview.incomeSeries')} expenseLabel={t('finance.overview.expenseSeries')} />
              </Section>
              <Section title={t('finance.overview.categories')} icon={Scale} actions={<Badge tone="outline">{monthLabel(month)}</Badge>}>
                <CategoryDonut
                  total={o.expenseTotal}
                  centerLabel={t('finance.overview.totalExpense')}
                  slices={o.categories.map((c) => ({ id: c.categoryId, name: cat.get(c.categoryId)?.name ?? '—', amount: c.amount, pct: c.pct, color: cat.get(c.categoryId)?.color ?? 1 }))}
                />
              </Section>
              <Section title={t('finance.overview.accrual')} className="lg:col-span-2">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_1fr_minmax(0,1.3fr)]">
                  {(['income', 'expense', 'result'] as const).map((k) => (
                    <div key={k} className="min-w-0 space-y-1 lg:border-r lg:pr-4">
                      <p className="text-sm text-muted-foreground">{t(`finance.overview.accrual_${k}`)}</p>
                      <p className="text-xl font-semibold">
                        <Money value={o.accrual[k].value} signed={k === 'result'} />
                      </p>
                      <TrendChip trend={o.accrual[k]} goodWhenUp={k !== 'expense'} />
                    </div>
                  ))}
                  <InfoNote className="p-3">{t('finance.overview.accrualHint')}</InfoNote>
                </div>
              </Section>
            </div>

            <Section title={t('finance.overview.taxTitle')} icon={Info}>
              <div className="space-y-3">
                <InfoNote className="p-3">{t('finance.overview.taxHint')}</InfoNote>
                <dl className="divide-y">
                  {(['vat', 'withholding', 'corporate'] as const).map((k) => (
                    <div key={k} className="flex items-start justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <dt className="font-medium">{t(`finance.overview.tax_${k}`, { period: monthLabel(month) })}</dt>
                        <p className="text-xs text-muted-foreground">{t(`finance.overview.tax_${k}Hint`)}</p>
                      </div>
                      <dd className="shrink-0 text-right">
                        <Money value={o.tax[k]} className="font-semibold" />
                        <Badge tone="warning" className="mt-1 flex w-fit rounded-md">{t('finance.estimated')}</Badge>
                      </dd>
                    </div>
                  ))}
                </dl>
                <Link to={`/admin/tax?m=${month}`} className="flex items-center gap-3 rounded-lg border border-destructive/25 bg-destructive/[0.05] p-3 transition-colors hover:bg-destructive/[0.08]">
                  <FileWarning className="size-5 shrink-0 text-destructive" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-destructive">{t('finance.overview.missingDocs')}</span>
                    <span className="block text-xs text-muted-foreground">{t('finance.overview.missingDocsHint')}</span>
                  </span>
                  <span className="text-xl font-semibold tabular-nums text-destructive">{o.missingDocs}</span>
                  <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                </Link>
              </div>
            </Section>
          </div>

          <CategoryDetail rows={o.categoryDetail} month={month} />
        </div>
      )}

      <TransactionFormModal open={txForm} transaction={null} defaultKind="expense" onOpenChange={setTxForm} />
    </>
  )
}
